import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Stack, Row, Text, Badge, Panel, Button, Tag, Divider } from '../ui';
import { api } from '../api/client';

const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

const KYC_SEVERITY_COLOR: Record<string, string> = { green: 'bg-positive', yellow: 'bg-amber-400', red: 'bg-negative' };
const KYC_LABEL: Record<string, string> = { green: 'Clean', yellow: 'Caution', red: 'High risk' };

function KycReportPanel({ opportunity }: { opportunity: any }) {
  const [reportMd, setReportMd] = useState<string | null>(null);
  const kycSeverity = opportunity.kycSeverity;
  const reportArtId = opportunity.kycReportArtifactId;
  const kycDone = !!kycSeverity && !!reportArtId;

  useEffect(() => {
    if (!reportArtId) return;
    api.artifactContent(reportArtId).then(r => setReportMd(r.contentMd ?? null)).catch(() => {});
  }, [reportArtId]);

  return (
    <Panel title="KYC & Due Diligence Report">
      <Stack gap={2}>
        {!kycDone ? (
          <Row gap={2}>
            <span className="h-2 w-2 rounded-full bg-muted animate-pulse shrink-0 mt-1" />
            <Text variant="small" muted>KYC screening in progress — usually completes within 60 seconds.</Text>
          </Row>
        ) : (
          <>
            <Row gap={2}>
              <span className={`h-3 w-3 rounded-full ${KYC_SEVERITY_COLOR[kycSeverity] ?? 'bg-muted'} shrink-0 mt-0.5`} />
              <Text variant="small" className="font-medium">{KYC_LABEL[kycSeverity] ?? kycSeverity}</Text>
            </Row>
            {reportMd && (
              <div className="prose prose-sm max-w-none bg-whisper rounded-sm p-3 text-xs whitespace-pre-wrap font-mono">
                {reportMd.slice(0, 3000)}{reportMd.length > 3000 ? '\n\n[truncated — download full report]' : ''}
              </div>
            )}
            {opportunity.cisArtifactId && (
              <a href={`${API}/vault/${opportunity.cisArtifactId}/download`} className="text-xs text-accent hover:underline">Download CIS document (encrypted) →</a>
            )}
            {opportunity.scoArtifactId && (
              <a href={`${API}/vault/${opportunity.scoArtifactId}/download`} className="text-xs text-accent hover:underline">Download SCO document (encrypted) →</a>
            )}
            {opportunity.fcoArtifactId && (
              <a href={`${API}/vault/${opportunity.fcoArtifactId}/download`} className="text-xs text-accent hover:underline">Download FCO document (encrypted) →</a>
            )}
          </>
        )}
      </Stack>
    </Panel>
  );
}

function statusTone(s: string): 'neutral'|'positive'|'negative'|'warning' {
  if (s === 'accepted') return 'positive';
  if (s === 'rejected') return 'negative';
  if (s === 'pending_review') return 'warning';
  return 'neutral';
}

export function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');

  const refresh = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api.opportunity(id).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);

  async function decide(decision: 'accepted'|'rejected') {
    if (!id) return;
    setDeciding(true);
    try { await api.externalDecision(id, decision, decisionNote || undefined); refresh(); } finally { setDeciding(false); }
  }

  if (loading) return <Text muted variant="small">loading...</Text>;
  if (!data) return <Stack gap={2}><Link to="/opportunities" className="text-xs text-muted hover:text-ink">← opportunities</Link><Text variant="display">not found</Text></Stack>;

  const { opportunity: o } = data;
  const isExternal = o.source === 'external';
  const isPending = o.externalStatus === 'pending_review';

  return (
    <Stack gap={3}>
      <Link to="/opportunities" className="text-xs text-muted hover:text-ink">← opportunities</Link>
      <Row justify="between" align="start" wrap>
        <Stack gap={0.5}>
          <Text variant="display">{o.title}</Text>
          <Row gap={1} wrap>
            {isExternal && <Badge tone={statusTone(o.externalStatus)}>{o.externalStatus === 'pending_review' ? 'pending review' : o.externalStatus}</Badge>}
            {o.sector && <Tag>{o.sector}</Tag>}
            {o.geography && <Tag>{o.geography}</Tag>}
          </Row>
        </Stack>
      </Row>
      {isExternal && <KycReportPanel opportunity={o} />}
      {isExternal && (
        <Panel title="Submitted by">
          <div className="rounded-sm bg-whisper p-4">
            <Stack gap={1}>
              <Row gap={3} wrap>
                {o.externalSubmitterName && <Stack gap={0.5}><Text variant="label" muted>Name</Text><Text variant="small" className="font-medium">{o.externalSubmitterName}</Text></Stack>}
                {o.externalSubmitterCompany && <Stack gap={0.5}><Text variant="label" muted>Company</Text><Text variant="small" className="font-medium">{o.externalSubmitterCompany}</Text></Stack>}
              </Row>
              <Row gap={3} wrap>
                {o.externalSubmitterEmail && <Stack gap={0.5}><Text variant="label" muted>Email</Text><a href={`mailto:${o.externalSubmitterEmail}`} className="text-sm text-ink hover:underline">{o.externalSubmitterEmail}</a></Stack>}
                {o.externalSubmitterPhone && <Stack gap={0.5}><Text variant="label" muted>Phone</Text><Text variant="small">{o.externalSubmitterPhone}</Text></Stack>}
              </Row>
            </Stack>
          </div>
          {isPending && (
            <>
              <Divider />
              <Stack gap={2}>
                <Text variant="label" className="font-medium">Review decision</Text>
                <textarea className="w-full px-3 py-2 text-sm hairline rounded-sm bg-bg resize-none" rows={2} placeholder="Optional note..." value={decisionNote} onChange={e => setDecisionNote(e.target.value)} />
                <Row gap={2}>
                  <Button variant="primary" disabled={deciding} onClick={() => {
                    if (o.kycSeverity === 'red') {
                      if (!confirm('This submission has CRITICAL compliance flags. Are you sure you want to accept?')) return;
                    }
                    decide('accepted');
                  }}>Accept submission</Button>
                  <Button variant="secondary" disabled={deciding} onClick={() => decide('rejected')}>Reject</Button>
                </Row>
              </Stack>
            </>
          )}
        </Panel>
      )}
      <Panel title="Details">
        <Stack gap={3}>
          {o.description && <div><Text variant="label" muted className="mb-1">Description</Text><Text variant="small" className="whitespace-pre-wrap">{o.description}</Text></div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {o.sector && <Stack gap={0.5}><Text variant="label" muted>Sector</Text><Text variant="small">{o.sector}</Text></Stack>}
            {o.geography && <Stack gap={0.5}><Text variant="label" muted>Geography</Text><Text variant="small">{o.geography}</Text></Stack>}
            {o.productGrade && <Stack gap={0.5}><Text variant="label" muted>Product / grade</Text><Text variant="small">{o.productGrade}</Text></Stack>}
            {o.ticketSizeRange && <Stack gap={0.5}><Text variant="label" muted>Ticket size</Text><Text variant="small">{o.ticketSizeRange}</Text></Stack>}
            {o.status && <Stack gap={0.5}><Text variant="label" muted>Status</Text><Tag>{o.status}</Tag></Stack>}
          </div>
          {o.rolesNeeded && o.rolesNeeded.length > 0 && (
            <div><Text variant="label" muted className="mb-2">Roles needed</Text><Row gap={1} wrap>{(o.rolesNeeded as string[]).map((r: string) => <Tag key={r}>{r}</Tag>)}</Row></div>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
}
